import { useEffect } from 'react';
import { t } from '@lingui/macro';
import { useQueryClient } from '@tanstack/react-query';
import { Location } from 'history';
import cloneDeep from 'lodash-es/cloneDeep';
import set from 'lodash-es/set';

import AddToShortlistButton from '@rover/components/src/shortlist/components/AddToShortlistButton';
import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import { Box, Button } from '@rover/kibble/core';
import { Props as ButtonProps } from '@rover/kibble/core/Button';
import { SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import { getApiPersonFullSitterProfileRetrieveQueryKey } from '@rover/rsdk/src/apiClient/latest/api-person-full-sitter-profile-retrieve';
import { useApiRelationshipDetailUpdate } from '@rover/rsdk/src/apiClient/latest/api-relationship-detail-update';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import emitMetric from '@rover/utilities/emitMetric';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';
import { redirect } from '@rover/utilities/url';

import { OwnerConversionSitterProfileContactSitterEvent } from '../SitterProfilePage/analyticsEvents';

type Props = ButtonProps & {
  sitterInfo: EnhancedPersonSitterProfile | undefined;
  sitterSlug: string;
  contactUrl: string;
  isLoggedIn?: boolean;
  location: Location;
  pagePosition: 'main' | 'sticky-footer' | 'sidebar';
  hideSitterName?: boolean;
  shouldShowRevampLayout?: boolean;
};

const ContactButton = ({
  sitterInfo,
  sitterSlug,
  contactUrl,
  isLoggedIn,
  location,
  pagePosition,
  shouldShowRevampLayout = false,
  hideSitterName = false,
  ...other
}: Props): JSX.Element => {
  const queryClient = useQueryClient();
  const { isSuccess, data } = useApiRelationshipDetailUpdate({
    mutation: { retry: 3 },
  });
  const { isVisible: isShortlistVisible } = useShortlist();
  // getting service for the react-query key
  const search = new URLSearchParams(location.search);
  const service = search.get('service_type');
  const serviceParams = service ? { service } : undefined;
  const nocache = search.get('no_cache');
  const nocacheParams = nocache ? { no_cache: '1' } : undefined;
  const isHeader = pagePosition === 'main' && shouldShowRevampLayout;

  useEffect(() => {
    if (isSuccess) {
      const queryKey = getApiPersonFullSitterProfileRetrieveQueryKey({
        slug: sitterSlug,
        ...serviceParams,
        ...nocacheParams,
      });
      const updatedData = cloneDeep(sitterInfo);
      set(updatedData, 'authenticatedContext.isFavorite', data.isFavorite);
      queryClient.setQueryData(queryKey, updatedData);
    }
  }, [isSuccess]);

  const handleOnPressContact = (): void => {
    emitMetric('pages_sitter_profile_contact_sitter_pressed', { pagePosition });
    Logger.info('pages_sitter_profile_contact_sitter_pressed');
    const pageVariantProps = shouldShowRevampLayout ? { pageVariant: 'revamp' as const } : {};
    storeCrossPageAnalyticsEvent(
      new OwnerConversionSitterProfileContactSitterEvent({ pagePosition, ...pageVariantProps })
    );
    if (!isLoggedIn) redirect('/account/continue/');
    redirect(contactUrl);
  };

  const hyphenatedSuffix = pagePosition ? `-${pagePosition}` : '';
  const shortlistSuffix = isShortlistVisible ? '-shortlist' : '';
  const testId = `contact-button${shortlistSuffix}${hyphenatedSuffix}`;
  const sitterName = sitterInfo?.shortName;
  const buttonText = hideSitterName ? t`Contact` : t`Contact ${sitterName}`;
  const commonButtonProps = {
    width: isHeader ? undefined : '100%',
    px: isHeader ? ('6x' as SpacingSystemType) : undefined,
    'data-testid': testId,
  };

  if (isShortlistVisible) {
    return (
      <AddToShortlistButton
        personOpk={sitterInfo?.opk ?? ''}
        source="sitter-profile"
        removedText={buttonText}
        removedIcon={null}
        removedVariant="primary"
        maxWidth={isHeader ? '250px' : undefined}
        {...commonButtonProps}
        {...other}
      />
    );
  }

  return (
    <Button
      variant="primary"
      maxWidth={isHeader ? '200px' : undefined}
      onClick={handleOnPressContact}
      {...commonButtonProps}
      {...other}
    >
      <Box
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {buttonText}
      </Box>
    </Button>
  );
};

export default ContactButton;
